import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatDuration, formatTokens, usageTotal } from '../lib/format';
import { sumStepUsage } from '../lib/run-state';
import type { ChatMessage, RunState } from '../lib/types';
import { InspectIcon } from './Icons';

interface ConversationProps {
  messages: ChatMessage[];
  selectedRunId?: string;
  agentName: string;
  onSelectRun: (runId: string) => void;
  onExample: (prompt: string) => void;
}

const examples = [
  'What time is it in Tokyo? Use the right specialist and show your process.',
  'Roll two six-sided dice, calculate the total, and explain each step.',
  'Rewrite this in one crisp sentence: Our team is working to improve the experience for every customer.',
];

function elapsed(run: RunState): number {
  return (run.endedAt ?? Date.now()) - run.startedAt;
}

function RunStatus({ run }: { run: RunState }) {
  const active = [...run.trace].reverse().find((entry) => entry.status === 'running');
  if (run.status === 'streaming') {
    return (
      <div className="live-phase">
        <span className="live-pulse" />
        <span>{active?.label ?? 'Receiving stream'}</span>
      </div>
    );
  }
  if (run.status === 'error') return <span className="run-error-label">Run failed</span>;
  if (run.status === 'aborted') return <span>Stopped</span>;
  if (run.status === 'restored') return <span>Restored from memory</span>;
  return <span>Completed in {formatDuration(elapsed(run))}</span>;
}

function Answer({ run }: { run: RunState }) {
  if (!run.answer && run.status === 'streaming') {
    return (
      <div className="answer-pending">
        <span /><span /><span />
        <p>The answer will appear here while the execution trace builds beside it.</p>
      </div>
    );
  }
  if (!run.answer && run.status === 'error') {
    return <p className="answer-error">{run.error ?? 'The run failed before returning an answer.'}</p>;
  }
  if (!run.answer && run.status === 'aborted') return <p className="answer-muted">Run stopped before an answer was returned.</p>;

  return (
    <div className={`markdown-answer${run.status === 'streaming' ? ' is-streaming' : ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ children, ...props }) => <a {...props} target="_blank" rel="noreferrer">{children}</a>,
        }}
      >
        {run.answer}
      </ReactMarkdown>
    </div>
  );
}

function AssistantTurn({
  run,
  selected,
  onSelect,
}: {
  run: RunState;
  selected: boolean;
  onSelect: () => void;
}) {
  const usage = run.totalUsage ?? sumStepUsage(run.steps);
  const tokenCount = usageTotal(usage);
  return (
    <article className={`assistant-turn${selected ? ' selected' : ''}`} aria-label={`${run.agentId} response`}>
      <div className="turn-gutter">
        <span className="turn-agent-mark">M</span>
        <span className="turn-line" />
      </div>
      <div className="turn-content">
        <div className="turn-heading">
          <div>
            <strong>{run.agentId}</strong>
            <RunStatus run={run} />
          </div>
          <button
            className="inspect-run-button"
            type="button"
            onClick={onSelect}
            aria-pressed={selected}
            aria-label={selected ? 'Inspecting run' : 'Inspect run'}
          >
            <InspectIcon />
            <span>{selected ? 'Inspecting' : 'Inspect run'}</span>
          </button>
        </div>
        <Answer run={run} />
        <div className="run-glance" aria-label="Run summary">
          <span><b>{run.totalEvents}</b> events</span>
          <span><b>{run.steps.length}</b> model {run.steps.length === 1 ? 'step' : 'steps'}</span>
          <span><b>{run.tools.length}</b> {run.tools.length === 1 ? 'tool' : 'tools'}</span>
          <span><b>{formatTokens(tokenCount)}</b> tokens</span>
        </div>
      </div>
    </article>
  );
}

export function Conversation(props: ConversationProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const shouldFollow = useRef(true);

  useEffect(() => {
    if (shouldFollow.current) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [props.messages]);

  return (
    <div
      className="conversation-viewport"
      ref={viewportRef}
      onScroll={() => {
        const element = viewportRef.current;
        if (!element) return;
        shouldFollow.current = element.scrollHeight - element.scrollTop - element.clientHeight < 180;
      }}
    >
      <div className="conversation">
        {props.messages.length === 0 ? (
          <section className="empty-state">
            <div className="empty-index">LIVE / 001</div>
            <h1>Ask the brain.<br /><em>Watch the run.</em></h1>
            <p>
              Every answer arrives with its execution trail: routing, model reasoning,
              tool calls, validation, tokens, cost, and the underlying Mastra events.
            </p>
            <div className="example-list" aria-label="Example prompts">
              {examples.map((example, index) => (
                <button key={example} type="button" onClick={() => props.onExample(example)}>
                  <span>0{index + 1}</span>
                  <span>{example}</span>
                </button>
              ))}
            </div>
          </section>
        ) : (
          props.messages.map((message) => message.role === 'user' ? (
            <article className="user-turn" key={message.id}>
              <div className="user-label">You</div>
              <p>{message.text}</p>
            </article>
          ) : (
            <AssistantTurn
              key={message.id}
              run={message.run}
              selected={message.run.id === props.selectedRunId}
              onSelect={() => props.onSelectRun(message.run.id)}
            />
          ))
        )}
        <div ref={bottomRef} className="scroll-anchor" />
      </div>
    </div>
  );
}
